import { ChangeDetectionStrategy, Component, DestroyRef, ViewEncapsulation, computed, effect, forwardRef, inject, input, signal, untracked } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, forkJoin, tap } from 'rxjs';
import { ActivityEntityName, SystemLabels, WorkOrderStatusUtility } from '@jms/model';
import { AvatarMode } from '@workbuddy/ui/components/avatar';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { SettingsPanelPickerComponent, SettingsPanelPickerViewConfig } from '../picker/settings.panel.picker.component';
import { SettingsPanelMode, SettingsPanelSelectOption } from '../model';
import { PickerOptionId } from '../picker/picker.selection.utils';

export interface EntityPickerViewConfig {
  entity: ActivityEntityName | ActivityEntityName[];
  mode: 'single' | 'multi';
  entityOptions?: Record<string, unknown>;
  groupLabel?: (entity: ActivityEntityName) => string;
  selectedOptions?: SettingsPanelSelectOption[];
}

@Component({
  selector: 'settings-panel-entity-picker',
  standalone: true,
  imports: [SettingsPanelPickerComponent, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `<settings-panel-picker [config]="$pickerConfig()" [mode]="$mode()" [active]="$active()" [formControl]="control" />`,
  styleUrls: ['./settings.panel.entity.picker.component.less'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SettingsPanelEntityPickerComponent), multi: true }],
})
export class SettingsPanelEntityPickerComponent implements ControlValueAccessor {

  private readonly entityService = inject(EntityDataServiceRaw);
  private readonly destroyRef = inject(DestroyRef);

  public $config = input.required<EntityPickerViewConfig>({ alias: 'config' });
  public $mode = input<SettingsPanelMode>(SettingsPanelMode.Sidebar, { alias: 'mode' });
  public $active = input<boolean>(true, { alias: 'active' });

  protected $options = signal<SettingsPanelSelectOption[]>([]);
  protected control = new FormControl();
  private lastLoadKey: string | null = null;

  private _onChange: (value: PickerOptionId | PickerOptionId[]) => void = () => {};
  private _onTouched: () => void = () => {};

  private $entities = computed(() => {

    const entity = this.$config().entity;
    return Array.isArray(entity) ? entity : [entity];
  });

  protected $pickerConfig = computed<SettingsPanelPickerViewConfig>(() => ({
    options: this.$options,
    mode: this.$config().mode,
    searchable: true,
    onSearch: (query: string) => this.searchSubject$.next(query),
  }));

  private searchSubject$ = new BehaviorSubject<string | null>(null);

  public constructor() {

    this.searchSubject$.pipe(takeUntilDestroyed(), filter(s => s !== null), debounceTime(300), distinctUntilChanged(), tap(() => this.loadData())).subscribe();

    // Load data when entity/entityOptions change
    effect(() => {

      const entities = this.$entities();
      const entityOptions = untracked(() => this.$config().entityOptions ?? null);
      const loadKey = JSON.stringify({ entities, entityOptions });

      untracked(() => {
        if (this.lastLoadKey === loadKey) return;
        this.lastLoadKey = loadKey;
        this.loadData();
      });
    });

    // Propagate inner control changes to parent CVA
    this.control.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      this._onChange(value);
      this._onTouched();
    });
  }

  // ── CVA ─────────────────────────────────────────────────────────────

  public writeValue(value: PickerOptionId | PickerOptionId[]): void {

    this.control.setValue(value, { emitEvent: false });
  }

  public registerOnChange(fn: (value: PickerOptionId | PickerOptionId[]) => void): void {

    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {

    this._onTouched = fn;
  }

  // ── Data loading ────────────────────────────────────────────────────

  private loadData() {

    const config = this.$config();
    if (!config?.entity) return;

    const entities = this.$entities();
    const isMultiEntity = entities.length > 1;

    const search = this.searchSubject$.value ?? undefined;
    const options = { ...config.entityOptions, search };

    const requests = entities.map(entity => this.entityService.getPicker(entity, options as any));

    forkJoin(requests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(results => {

      const pickerOptions: SettingsPanelSelectOption[] = [];

      results.forEach((data, index) => {

        const entityName = entities[index];
        const bindName = data?.bindName ?? 'name';
        const bindGroup = data?.bindGroup;
        const rawItems = this.applyEntitySpecificFilters(entityName, data?.items ?? []);
        const items: SettingsPanelSelectOption[] = rawItems.map((item: any) => {

          const label = item[bindName] ?? item.name ?? '';
          const icon = this.normalizeIcon(item.icon);
          const color = this.resolveItemColor(entityName, item);

          return {
            id: item._id ?? item.id,
            label,
            color,
            avatar: icon ? { value: icon, mode: AvatarMode.Icon, tooltip: label } : (data?.hasAvatar ? item.name : undefined),
            description: data?.bindDescription1 ? item[data.bindDescription1] : undefined,
            group: isMultiEntity ? (config.groupLabel?.(entityName) ?? entityName) : (bindGroup ? item[bindGroup] : undefined),
            data: item,
          };
        });
        pickerOptions.push(...items);
      });

      this.$options.set(this.mergeSelectedOptions(pickerOptions, config.selectedOptions ?? []));
    });
  }

  private mergeSelectedOptions(options: SettingsPanelSelectOption[], selectedOptions: SettingsPanelSelectOption[]): SettingsPanelSelectOption[] {

    if (!selectedOptions.length) return options;

    const existingIds = new Set(options.map(option => option.id));
    const missingSelectedOptions = selectedOptions.filter(option => !existingIds.has(option.id));

    return [...missingSelectedOptions, ...options];
  }

  private normalizeIcon(icon: unknown): string | null {

    if (typeof icon !== 'string' || !icon.trim()) return null;
    return icon.includes('fa-') && !icon.includes('fa-regular') && !icon.includes('fa-solid') ? `fa-solid ${icon}` : icon;
  }

  private applyEntitySpecificFilters(entity: ActivityEntityName, items: any[]): any[] {

    // Tags: hide system labels — users can't add or remove these via bulk actions.
    if (entity === ActivityEntityName.Tag) {
      return items.filter(item => !SystemLabels.isSystem(item));
    }

    return items;
  }

  private resolveItemColor(entity: ActivityEntityName, item: any): string | undefined {

    if (item?.color) return item.color;

    // WorkOrderStatus rows don't carry a `color` field — derive it from the system status bucket.
    if (entity === ActivityEntityName.WorkOrderStatus && item?.systemStatus) {
      return WorkOrderStatusUtility.getSystemColor(item.systemStatus);
    }

    return undefined;
  }
}
